import {
  CssBaseline,
  Typography,
  Grid,
  Card,
  CardActionArea,
  CardActions,
  CardContent,
  IconButton,
  Link,
  Tooltip,
  FormControl,
  Select,
  MenuItem,
} from "@material-ui/core";
import { useState } from "react";
import { Helmet } from "react-helmet";
import { Theme, makeStyles, createStyles } from "@material-ui/core/styles";
import Copyright from "./Copyright";
import NavigationBar from "../components/NavigationBar";
import { WhatsApp, MailOutlined, Call } from "@material-ui/icons";
import TopBar from "../components/TopBar";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    actionArea: {
      "&:hover $focusHighlight": {
        opacity: 0,
        background: "transparent",
      },
      height: "fit-content",
    },
    infoGrid: {
      [theme.breakpoints.down("xs")]: {
        marginBottom: "0.25rem",
      },
      [theme.breakpoints.up("sm")]: {
        marginBottom: "0.25rem",
      },
      [theme.breakpoints.up("md")]: {
        marginBottom: "0.25rem",
      },
      [theme.breakpoints.up("lg")]: {
        marginBottom: "0.25rem",
      },
    },
    card: {
      background:
        "linear-gradient(135deg, #ecfaf3 25%, #e0faf8 75%, rgb(193 248 255) 100%)",
      borderRadius: "1rem",
      display: "grid",
      [theme.breakpoints.between(0, 320)]: {
        width: 300,
        height: 300,
      },
      [theme.breakpoints.between(321, 400)]: {
        width: 325,
        height: 300,
      },
      [theme.breakpoints.between(401, 500)]: {
        width: 375,
        height: 300,
      },
      [theme.breakpoints.between(501, 600)]: {
        width: 475,
        height: 300,
      },
      [theme.breakpoints.between(601, 700)]: {
        width: 575,
        height: 250,
      },
      [theme.breakpoints.between(701, 800)]: {
        width: 675,
        height: 250,
      },
      [theme.breakpoints.between(801, 900)]: {
        width: 775,
        height: 250,
      },
      [theme.breakpoints.between(901, 1000)]: {
        width: 800,
        height: 250,
      },
      [theme.breakpoints.between(1001, 1200)]: {
        width: 800,
        height: 250,
      },
      [theme.breakpoints.up(1201)]: {
        width: 800,
        height: 250,
      },
    },
    title1: {
      marginTop: "1rem",
      justifyContent: "center",
      textAlign: "center",
      fontSize: 28,
      fontWeight: 700,
      color: "white",
      textShadow: "0.5px 0.5px 0.5px #ffeb3b",
    },
    title2: {
      marginTop: "1rem",
      justifyContent: "center",
      textAlign: "center",
      fontSize: 28,
      fontWeight: 700,
      color: "black",
      textShadow: "0.5px 0.5px 0.5px #ffeb3b",
    },
    content: {
      justifyContent: "center",
      textAlign: "center",
      fontSize: 12,
      fontWeight: 400,
    },
    action: {
      justifyContent: "center",
      textAlign: "center",
      height: "fit-content",
    },
    button: {
      color: "white",
      background: "rgba(202,25,85,1)",
      borderRadius: "12px",
      fontWeight: "bold",
      padding: "0.25rem",
      textDecoration: "none",
      height: "3rem",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      [theme.breakpoints.down("sm")]: {
        fontSize: 20,
        width: "11rem",
        height: "2.25rem",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      },
      [theme.breakpoints.up("md")]: {
        fontSize: 20,
        width: "11rem",
        height: "2.25rem",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      },
      [theme.breakpoints.up("lg")]: {
        fontSize: 20,
        width: "11rem",
        height: "2.25rem",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      },
    },
    selectField: {
      display: "flex",
      background: "white",
      borderRadius: 30,
      maxWidth: 440,
      width: "90%",
      margin: "0 auto",
      boxShadow: "0 6px 18px rgba(0,0,0,0.15)",
      "& .MuiOutlinedInput-root": {
        borderRadius: 30,
      },
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: "transparent",
      },
      "& .MuiSelect-select": {
        fontWeight: 600,
        textAlign: "center",
        paddingLeft: "2rem",
      },
    },
  }),
);

// Klacht -> aanbevolen therapeut. Pas deze tabel hier aan als de verdeling wijzigt.
const THERAPEUTEN: Record<
  string,
  { naam: string; rol: string; werkdagen: string }
> = {
  emilie: {
    naam: "Emilie Wegman",
    rol: "Manueel & orofaciaal therapeut",
    werkdagen: "maandag, dinsdag, donderdag en zaterdag",
  },
  jimmy: {
    naam: "Jimmy Nirmalsingh",
    rol: "Sport- & revalidatiespecialist",
    werkdagen: "vrijdag",
  },
};

const KLACHTEN: { value: string; label: string; advies: string }[] = [
  { value: "rug", label: "Rug", advies: "emilie" },
  { value: "nek", label: "Nek", advies: "emilie" },
  { value: "hoofdpijn", label: "Hoofdpijn", advies: "emilie" },
  { value: "duizeligheid", label: "Duizeligheid", advies: "emilie" },
  { value: "kaak", label: "Kaak", advies: "emilie" },
  { value: "knie", label: "Knie", advies: "jimmy" },
  { value: "enkel", label: "Enkel", advies: "jimmy" },
  { value: "heup", label: "Heup", advies: "jimmy" },
  { value: "schouder", label: "Schouder", advies: "jimmy" },
  { value: "elleboog", label: "Elleboog", advies: "jimmy" },
  { value: "pols", label: "Pols", advies: "jimmy" },
  { value: "sport", label: "Sportblessure", advies: "jimmy" },
  { value: "operatie", label: "Revalidatie na operatie", advies: "jimmy" },
];

function Afspraken() {
  const classes = useStyles();
  const [klacht, setKlacht] = useState("");
  const advies = KLACHTEN.find((k) => k.value === klacht)?.advies;
  const aanbevolen = advies ? [THERAPEUTEN[advies]].filter(Boolean) : [];
  return (
    <div style={{ background: "white", justifyContent: "center" }}>
      <Helmet>
        <meta charSet="utf-8" />
        <title>
          BOOST Fysiotherapie / Afspraak / Manuele therapie / Dry needling
        </title>
        <meta
          name="description"
          content="Contact Adres: Elemastraat 65 2151 AM Nieuw Vennep Telefoon: 0618223861 Email: info@boostfysiotherapie.nl Website: www.boostfysiotherapie.nl"
        />
        <meta
          property="og:title"
          content="BOOST Fysiotherapie / Afspraak / Manuele therapie / Dry needling / Orofaciaal therapie"
        />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="/afspraken" />
      </Helmet>

      <CssBaseline />

      <TopBar />
      <NavigationBar />

      <Grid
        container
        justifyContent="center"
        alignItems="center"
        style={{
          marginTop: "1rem",
          gap: "1rem",
          minHeight: "calc(100vh - 64px - 48px)",
          paddingBottom: "3.5rem",
        }}
        direction="row"
      >
        <Grid
          container
          item
          direction="column"
          alignItems="center"
          className={classes.infoGrid}
        >
          <Card
            className={classes.card}
            style={{
              height: "auto",
              background:
                "linear-gradient(180deg, rgb(1 183 168) 25%, rgb(149 248 240) 75%, rgb(132 240 231) 100%)",
            }}
          >
            <CardActionArea className={classes.actionArea}>
              <CardContent>
                <Typography component="h1" className={classes.title1}>
                  Waar wilt u hulp bij?
                </Typography>
              </CardContent>
            </CardActionArea>

            <CardContent style={{ paddingTop: 0, paddingBottom: 0 }}>
              <FormControl
                variant="outlined"
                size="small"
                className={classes.selectField}
              >
                <Select
                  displayEmpty
                  value={klacht}
                  onChange={(e) => setKlacht(e.target.value as string)}
                >
                  <MenuItem value="" disabled>
                    Kies uw klacht…
                  </MenuItem>
                  {KLACHTEN.map((k) => (
                    <MenuItem key={k.value} value={k.value}>
                      {k.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {aanbevolen.length > 0 && (
                <div
                  style={{
                    marginTop: "0.75rem",
                    maxWidth: 420,
                    marginLeft: "auto",
                    marginRight: "auto",
                    background: "rgba(255,255,255,0.92)",
                    borderRadius: 12,
                    padding: "0.75rem",
                    textAlign: "left",
                  }}
                >
                  <Typography
                    style={{ fontSize: 13, fontWeight: 700, color: "#07006f" }}
                  >
                    Uw klacht past goed bij de expertise van:
                  </Typography>
                  {aanbevolen.map((t) => (
                    <Typography
                      key={t.naam}
                      style={{ fontSize: 13, color: "black", marginTop: 4 }}
                    >
                      <strong>{t.naam}</strong> — {t.rol}
                      <br />
                      <span style={{ color: "#3d072d" }}>
                        Werkdagen: {t.werkdagen}
                      </span>
                    </Typography>
                  ))}
                  <Typography
                    style={{ fontSize: 11, color: "#333", marginTop: 6 }}
                  >
                    Dit is een advies. U kiest zelf uw therapeut bij het maken
                    van de afspraak. Twijfelt u nog? Mail of WhatsApp ons
                    gerust.
                  </Typography>
                </div>
              )}
            </CardContent>

            <CardActions className={classes.action}>
              <Grid container justifyContent="center" direction="row">
                <Grid container justifyContent="center" direction="row">
                  <Link href="/afspraak" className={classes.button}>
                    Maak Afspraak
                  </Link>
                </Grid>
                <Grid container justifyContent="center" direction="row">
                  <Typography
                    variant="h1"
                    component="h2"
                    style={{ fontSize: 12, color: "black", fontWeight: 500 }}
                  >
                    Indien u een behandeling aan huis wilt; neem dan contact op
                    via
                    <Tooltip
                      disableFocusListener
                      title="Phone"
                      style={{ padding: "0.2rem" }}
                    >
                      <IconButton href="tel:+31618223861">
                        <Call style={{ fontSize: 12, color: "#2196f3" }} />
                      </IconButton>
                    </Tooltip>{" "}
                    of
                    <Tooltip
                      disableFocusListener
                      title="Whatsapp"
                      style={{ padding: "0.2rem" }}
                    >
                      <IconButton href="https://api.whatsapp.com/send/?phone=31618223861&text=Boost%20Fysiotherapie,%20hoe%20kunnen%20we%20helpen?&&app_absent=0">
                        <WhatsApp style={{ fontSize: 12, color: "green" }} />
                      </IconButton>
                    </Tooltip>{" "}
                    of
                    <Tooltip
                      disableFocusListener
                      title="Email"
                      style={{ padding: "0.2rem" }}
                    >
                      <IconButton href="mailto:info@boostfysiotherapie.nl?Subject=Afspraak%20annulering/wijziging">
                        <MailOutlined
                          style={{ fontSize: 12, color: "#e91e63" }}
                        />
                      </IconButton>
                    </Tooltip>
                    . Deze behandeling kunt u niet via het afspraakportaal
                    inplannen.
                  </Typography>
                </Grid>
              </Grid>
            </CardActions>
            <CardContent style={{ textAlign: "center" }}>
              <Typography
                variant="h1"
                component="h2"
                style={{ fontSize: 12, color: "black", fontWeight: 500 }}
              >
                * U kunt informatie over verzekeringen en onze tarieven{" "}
                <Link href="/tarieven">
                  <u>
                    {" "}
                    <strong>hier</strong>
                  </u>
                </Link>{" "}
                vinden.
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid
          container
          item
          direction="column"
          alignItems="center"
          className={classes.infoGrid}
        >
          <Card
            className={classes.card}
            style={{
              background:
                "linear-gradient(135deg, #ff865f 25%, #ff865f 75%, #ff865f 100%)",
            }}
          >
            <CardActionArea className={classes.actionArea}>
              <CardContent>
                <Typography component="h1" className={classes.title2}>
                  Wilt u een afspraak annuleren/wijzigen?
                </Typography>
              </CardContent>
            </CardActionArea>
            <CardActions className={classes.action}>
              <Tooltip disableFocusListener title="Whatsapp">
                <IconButton href="https://api.whatsapp.com/send/?phone=31618223861&text=Boost%20Fysiotherapie,%20hoe%20kunnen%20we%20helpen?&&app_absent=0">
                  <WhatsApp style={{ fontSize: 40, color: "green" }} />
                </IconButton>
              </Tooltip>
              <Tooltip disableFocusListener title="Email">
                <IconButton href="mailto:info@boostfysiotherapie.nl?Subject=Afspraak%20annulering/wijziging">
                  <MailOutlined style={{ fontSize: 40, color: "#e91e63" }} />
                </IconButton>
              </Tooltip>
              <Tooltip disableFocusListener title="Phone">
                <IconButton href="tel:+31618223861">
                  <Call style={{ fontSize: 40, color: "#2196f3" }} />
                </IconButton>
              </Tooltip>
            </CardActions>
            <CardContent style={{ textAlign: "center" }}>
              <Typography
                variant="body2"
                component="p"
                style={{ fontSize: 13, color: "#07006f", fontWeight: 500 }}
              >
                <strong>Let op:</strong> Annuleren van een afspraak kan tot
                uiterlijk <strong>24 uur</strong> van tevoren.
              </Typography>

              <Typography
                variant="body2"
                component="p"
                style={{ fontSize: 13, color: "#07006f", fontWeight: 500 }}
              >
                Bij latere annulering zijn wij genoodzaakt het tarief voor een
                gemiste afspraak in rekening te brengen.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid
        container
        justifyContent="center"
        style={{
          background: "black",
          position: "fixed",
          bottom: "0",
          width: "100%",
        }}
      >
        <Copyright />
      </Grid>
    </div>
  );
}

export default Afspraken;
